import type { DocumentFileType } from '@poseidon/types/types/document.type';

/**
 * Represents an embeddable platform configuration.
 * Used to detect share URLs and convert them to embed URLs.
 */
export interface EmbedPlatform {
  /** The document file type identifier */
  type: DocumentFileType;
  /** Human-readable platform name */
  name: string;
  /** Regex to match share URLs and extract the video/content ID (in capture group 1) */
  shareUrlPattern: RegExp;
  /**
   * Function to build the embed URL from the extracted ID.
   * @param id The extracted content ID
   * @param originalUrl The original URL (useful for platforms that need query params)
   */
  buildEmbedUrl: (id: string, originalUrl?: string) => string;
  /**
   * Optional validation for the extracted ID.
   * @param id The extracted ID to validate
   */
  validateId?: (id: string) => boolean;
}

/**
 * Result of detecting an embeddable platform from a URL.
 */
export interface EmbedDetectionResult {
  /** The matched platform configuration */
  platform: EmbedPlatform;
  /** The extracted content ID */
  id: string;
  /** The embed-ready URL */
  embedUrl: string;
}

/**
 * All supported embeddable platforms.
 * Order matters - first match wins.
 */
export const EMBED_PLATFORMS: EmbedPlatform[] = [
  {
    type: 'youtube',
    name: 'YouTube',
    shareUrlPattern: /(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=|shorts\/))([^#&?]{11})/,
    buildEmbedUrl: (id) => `https://www.youtube.com/embed/${id}`,
    validateId: (id) => id.length === 11,
  },
  {
    type: 'vimeo',
    name: 'Vimeo',
    shareUrlPattern: /vimeo\.com\/(?:channels\/[A-Za-z]+\/|groups\/[A-Za-z]+\/videos\/)?(\d+)/,
    buildEmbedUrl: (id) => `https://player.vimeo.com/video/${id}?transparent=0&title=0&byline=0&portrait=0`,
  },
  {
    type: 'loom',
    name: 'Loom',
    shareUrlPattern: /loom\.com\/share\/([a-zA-Z0-9]+)/,
    buildEmbedUrl: (id) => `https://www.loom.com/embed/${id}`,
  },
  {
    type: 'descript',
    name: 'Descript',
    shareUrlPattern: /share\.descript\.com\/(?:view|embed)\/([a-zA-Z0-9]+)/,
    buildEmbedUrl: (id) => `https://share.descript.com/embed/${id}`,
  },
  {
    type: 'muse',
    name: 'Muse.ai',
    shareUrlPattern: /muse\.ai\/[vctd]\/([^#&?]+)/,
    buildEmbedUrl: (id) => `https://muse.ai/embed/${id}?volume=75`,
  },
  {
    type: 'canva',
    name: 'Canva',
    shareUrlPattern: /canva\.com\/design\/([^#&?/]+)\/[^?]*(?:\?|$)/,
    buildEmbedUrl: (id, originalUrl) => {
      // Canva uses the original URL with ?embed appended
      if (originalUrl) {
        const url = originalUrl.replace(/\?.*$/, '');
        return `${url}?embed`;
      }
      return `https://www.canva.com/design/${id}/view?embed`;
    },
  },
  // Future platforms can be added here:
  // {
  //   type: 'wistia',
  //   name: 'Wistia',
  //   shareUrlPattern: /wistia\.com\/medias\/([a-zA-Z0-9]+)/,
  //   buildEmbedUrl: (id) => `https://fast.wistia.net/embed/iframe/${id}`,
  // },
  // {
  //   type: 'vidyard',
  //   name: 'Vidyard',
  //   shareUrlPattern: /(?:share\.)?vidyard\.com\/watch\/([a-zA-Z0-9]+)/,
  //   buildEmbedUrl: (id) => `https://play.vidyard.com/${id}`,
  // },
];

/**
 * Detect if a URL is a YouTube clip (has clip parameter).
 * YouTube clips need special handling.
 */
function isYouTubeClip(url: string): boolean {
  try {
    const urlObj = new URL(url);
    return urlObj.searchParams.get('clip') != null;
  } catch {
    return false;
  }
}

/**
 * Detect an embeddable platform from a share URL and return embed info.
 *
 * @param url The share URL to detect
 * @returns Detection result with platform, ID, and embed URL, or null if not detected
 *
 * @example
 * ```typescript
 * const result = detectEmbedPlatform('https://www.youtube.com/watch?v=dQw4w9WgXcQ');
 * // { platform: {...}, id: 'dQw4w9WgXcQ', embedUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ' }
 *
 * const result = detectEmbedPlatform('https://www.loom.com/share/abc123def456');
 * // { platform: {...}, id: 'abc123def456', embedUrl: 'https://www.loom.com/embed/abc123def456' }
 * ```
 */
export function detectEmbedPlatform(url: string): EmbedDetectionResult | null {
  for (const platform of EMBED_PLATFORMS) {
    const match = platform.shareUrlPattern.exec(url);
    if (!match) continue;

    const id = match[1];
    if (id == null) continue;

    // Validate ID if validator exists
    if (platform.validateId && !platform.validateId(id)) {
      continue;
    }

    // Special handling for YouTube clips
    if (platform.type === 'youtube' && isYouTubeClip(url)) {
      return {
        platform: { ...platform, type: 'youtube-clip' },
        id,
        embedUrl: `https://www.youtube.com/clip/${id}`,
      };
    }

    return {
      platform,
      id,
      embedUrl: platform.buildEmbedUrl(id, url),
    };
  }

  return null;
}

/**
 * Get an embed platform configuration by type.
 *
 * @param type The document file type
 * @returns The platform configuration, or null if not found
 */
export function getEmbedPlatform(type: DocumentFileType): EmbedPlatform | null {
  // Handle youtube-clip as youtube
  const searchType = type === 'youtube-clip' ? 'youtube' : type;
  return EMBED_PLATFORMS.find((p) => p.type === searchType) ?? null;
}

/**
 * Check if a URL can be embedded (matches any known platform).
 *
 * @param url The URL to check
 * @returns true if the URL matches a known embeddable platform
 */
export function isEmbeddableUrl(url: string): boolean {
  return detectEmbedPlatform(url) != null;
}

/**
 * Get the document file type for a URL.
 * Returns 'web' if no specific platform is detected.
 *
 * @param url The URL to check
 * @returns The detected document file type
 */
export function getFileTypeFromUrl(url: string): DocumentFileType {
  const result = detectEmbedPlatform(url);
  return result?.platform.type ?? 'web';
}
